import { gql } from '@apollo/client';
import {
  Badge,
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalOverlay,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { useAssignConfigurationMutation } from '~components/displays/BulkAction/__generated__/BulkAssignConfiguration.graphql';
import { BulkAssignConfigurationInner } from '~components/displays/BulkAction/BulkAssignConfiguration';
import { SummaryCard } from '~components/displays/DisplayDetail/summary/SummaryCard';
import { ColorMap, LableMap } from '~components/displays/DisplaySyncStatus';
import CopyButton from '~components/ui/CopyButton';
import { LoadingIcon, SyncIcon } from '~components/ui/icons';
import { ConfigurationSyncStatus } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { MaybePromise } from '~utils/types';
import { ConfigurationCard_DisplayFragment } from './__generated__/ConfigurationCard.graphql';

interface Props {
  display: ConfigurationCard_DisplayFragment;
  refetchData: () => MaybePromise<void>;
}

export function ConfigurationCard({ display, refetchData }: Props) {
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions } = useAuth();
  const hasConfigurationUpdatePermission = verifyUserPermissions([Permission.ConfigurationUpdate]);
  const isComboDisplay = Boolean(display.host);
  const configName = display.configuration?.name ?? '';
  const hasConfiguration = !!display.configuration?.id;
  const lastSynced = display.configurationUpdatedAt
    ? format(new Date(display.configurationUpdatedAt), 'HH:mm:ss, do MMMM yyyy')
    : '';
  const syncStatus = display.configurationSyncStatus ?? 'OUT_OF_SYNC';
  const [AssignConfigurationMutation] = useAssignConfigurationMutation();
  const toast = useToast();
  const { t } = useTranslation();

  const setConfigurationModal = useDisclosure();

  const handleBulkAssignConfiguration = useCallback(async () => {
    try {
      if (display.configuration?.id && display.id) {
        await AssignConfigurationMutation({
          variables: {
            input: {
              displayIds: [display.id],
              id: display.configuration?.id,
            },
          },
        });
        await refetchData();
      }
      if (display.id) {
        analytics.track('configurationUpdate');
      } else {
        analytics.track('displayBulkActionComplete', {
          action: 'updateConfiguration',
          displayCount: 1,
        });
      }
    } catch (err) {
      toast({
        status: 'error',
        title: t('configurationSyncError'),
        description: fromError(err, 'bulkAssignConfiguration'),
      });
    }
  }, [
    display.configuration?.id,
    display.id,
    AssignConfigurationMutation,
    analytics,
    toast,
    t,
    refetchData,
  ]);

  if (isComboDisplay) return null;

  return (
    <>
      <SummaryCard title={t('configuration')}>
        {!hasConfiguration ? (
          <VStack spacing="5" alignItems="stretch">
            <HStack justifyContent="space-between" alignContent="start">
              <Box display="flex" flexDir="column">
                <Heading size="md" color="gray.900" mb="0" lineHeight="7">
                  {t('noConfiguration')}
                </Heading>
                <chakra.span color="gray.500">{t('noConfigurationDescription')}</chakra.span>
              </Box>
              <Button
                variant="outline"
                colorScheme="blue"
                size="sm"
                onClick={setConfigurationModal.onOpen}
                isDisabled={!hasConfigurationUpdatePermission}
              >
                {t('setConfiguration')}
              </Button>
            </HStack>
          </VStack>
        ) : (
          <VStack spacing={6} align="stretch">
            <Flex>
              <Box flex="1">
                <Heading size="md" mb={0}>
                  {t('configurationName')}
                </Heading>
                <HStack>
                  <Tooltip
                    bg="gray.700"
                    color="white"
                    fontSize="sm"
                    borderRadius="md"
                    placement="bottom-end"
                    label={configName}
                  >
                    <Text fontSize="md" fontWeight="medium" maxW="250px" isTruncated>
                      {configName}
                    </Text>
                  </Tooltip>
                  {configName}{' '}
                  {configName && (
                    <CopyButton value={configName} label={'configurationName'} ml="4" />
                  )}
                </HStack>
              </Box>
              <Box flex="1">
                <Heading size="md" mb={2}>
                  {t('status')}
                </Heading>
                {display.configurationSyncStatus && (
                  <HStack spacing={3}>
                    <Badge
                      textTransform="none"
                      px={5}
                      py={1}
                      borderRadius="xl"
                      bg={ColorMap[syncStatus]}
                      color="white"
                    >
                      <Flex align="center" gap={2}>
                        {LableMap[syncStatus]}
                        {syncStatus === ConfigurationSyncStatus.Syncing && (
                          <LoadingIcon color={'white'} />
                        )}
                      </Flex>
                    </Badge>
                    {syncStatus === ConfigurationSyncStatus.OutOfSync &&
                      hasConfigurationUpdatePermission && (
                        <Button
                          leftIcon={<SyncIcon />}
                          size="xs"
                          px={4}
                          py={1}
                          height="auto"
                          borderRadius="xl"
                          variant="outline"
                          onClick={() => {
                            handleBulkAssignConfiguration();
                          }}
                        >
                          {t('sync')}
                        </Button>
                      )}
                  </HStack>
                )}
              </Box>
            </Flex>
            <Box>
              <Heading size="md" mb={2}>
                {t('lastSynced')}
              </Heading>
              <Text fontSize="md">{lastSynced}</Text>
            </Box>
          </VStack>
        )}
      </SummaryCard>

      {setConfigurationModal.isOpen && (
        <Modal isOpen={setConfigurationModal.isOpen} onClose={setConfigurationModal.onClose}>
          <ModalOverlay />
          <BulkAssignConfigurationInner
            displays={[display]}
            onCancel={setConfigurationModal.onClose}
            onBack={setConfigurationModal.onClose}
            onSuccess={setConfigurationModal.onClose}
            hideBackButton={true}
          />
        </Modal>
      )}
    </>
  );
}

ConfigurationCard.graphql = {
  fragments: {
    ConfigurationCard_display: gql`
      fragment ConfigurationCard_display on Display {
        id
        host {
          coupledState
        }
        configuration {
          id
          name
        }
        groups {
          id
          name
        }
        bookmarks {
          all {
            reported
          }
        }
        platform {
          name
          version
          type
        }
        appSubscriptions {
          id
          name
        }
        configurationSyncStatus
        configurationUpdatedAt
      }
    `,
  },
  mutations: {
    BulkAssignConfiguration: gql`
      mutation assignConfiguration($input: DisplayBulkAssignConfigurationsInput!) {
        assignConfiguration(input: $input)
      }
    `,
  },
};
